import dayjs from 'dayjs';
import Highcharts from 'highcharts/highcharts-gantt';
import type {
  BuildGanttOptionsArgs, Irrigation, IrrigationKind, IrrigationStatus, ZoneIndex,
} from './riego.types';
import {
  CHART_UI,
  NOW_LINE,
  RUNNING_SOLID,
  RUNNING_TRACK,
  STATUS_COLORS,
  TOOLTIP_COLORS,
} from './dashboardColors';
import {
  CHART_HEADROOM,
  CHART_MIN_HEIGHT,
  HOVER_BRIGHTNESS,
  MS_PER_DAY,
  MS_PER_MINUTE,
  POINT_PADDING,
  PROGRESS_STATUSES,
  ROW_HEIGHT,
  STATUS_LABEL,
  TICK_PIXEL_INTERVAL,
} from './riego.constants';

const parseTime = (ts?: string): number => (ts ? new Date(ts).getTime() : NaN);

const fmt = (ms: number): string => new Date(ms).toLocaleString('es-CL', {
  weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
});

const dayBoundaries = (start: Date, end: Date): number[] => {
  const lines: number[] = [];
  let cursor = dayjs(start).startOf('day').add(1, 'day');
  const endMs = end.getTime();
  while (cursor.valueOf() < endMs) {
    lines.push(cursor.valueOf());
    cursor = cursor.add(1, 'day');
  }
  return lines;
};

const resolvePalette = (kind: IrrigationKind, status: IrrigationStatus) => {
  if (kind === 'scheduled') {
    return STATUS_COLORS[status === 'proposal' ? 'proposal' : 'scheduled'];
  }
  return STATUS_COLORS[status] || STATUS_COLORS.unknown;
};

const runningProgress = (start: number, end: number, nowMs: number): number => {
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  if (nowMs <= start) return 0;
  if (nowMs >= end) return 1;
  return (nowMs - start) / (end - start);
};

const buildPoints = (
  items: Irrigation[],
  zoneIndex: ZoneIndex,
  kind: IrrigationKind,
  nowMs: number,
): Highcharts.GanttPointOptionsObject[] => items
  .filter((it) => zoneIndex.has(it.zoneId))
  .map((it) => {
    const palette = resolvePalette(kind, it.status);
    const start = parseTime(it.initTime);
    const end = parseTime(it.endTime);
    const hasProgress = kind === 'real' && PROGRESS_STATUSES.has(it.status);
    const amount = hasProgress ? runningProgress(start, end, nowMs) : 0;
    const progressFill = it.status === 'running' ? RUNNING_SOLID : palette.fill;
    return {
      id: `${kind[0]}-${it.id}`,
      start,
      end,
      y: zoneIndex.get(it.zoneId) as number,
      color: hasProgress ? RUNNING_TRACK : palette.fill,
      borderColor: palette.border,
      ...(palette.dashed ? { dashStyle: 'Dash' as Highcharts.DashStyleValue } : null),
      ...(hasProgress ? { completed: { amount, fill: progressFill } } : null),
      custom: {
        kind, id: it.id, zoneId: it.zoneId, status: it.status,
      },
    } as Highcharts.GanttPointOptionsObject;
  });

const toMinutes = (hhmm?: string): number | null => {
  if (!hhmm) return null;
  const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(hhmm.trim());
  return match ? parseInt(match[1], 10) * 60 + parseInt(match[2], 10) : null;
};

const buildPeakHourBands = (
  rangeStart: Date,
  rangeEnd: Date,
  peakHour?: { init: string; end: string },
): Highcharts.XAxisPlotBandsOptions[] => {
  if (!peakHour) return [];
  const initMin = toMinutes(peakHour.init);
  const endMin = toMinutes(peakHour.end);
  if (initMin === null || endMin === null || initMin === endMin) return [];

  const rangeStartMs = rangeStart.getTime();
  const rangeEndMs = rangeEnd.getTime();
  const bands: Highcharts.XAxisPlotBandsOptions[] = [];

  const pushBand = (from: number, to: number) => {
    const clampedFrom = Math.max(from, rangeStartMs);
    const clampedTo = Math.min(to, rangeEndMs);
    if (clampedTo <= clampedFrom) return;
    bands.push({
      from: clampedFrom,
      to: clampedTo,
      color: CHART_UI.peakHourBand,
      zIndex: 0,
    });
  };

  let cursor = dayjs(rangeStart).startOf('day');
  const lastMs = rangeEnd.getTime();
  while (cursor.valueOf() <= lastMs) {
    const dayStart = cursor.valueOf();
    if (initMin < endMin) {
      pushBand(dayStart + initMin * MS_PER_MINUTE, dayStart + endMin * MS_PER_MINUTE);
    } else {
      // Cruza medianoche: madrugada [00:00, end] + noche [init, 24:00].
      pushBand(dayStart, dayStart + endMin * MS_PER_MINUTE);
      pushBand(dayStart + initMin * MS_PER_MINUTE, dayStart + MS_PER_DAY);
    }
    cursor = cursor.add(1, 'day');
  }
  return bands;
};

const buildDayLines = (
  rangeStart: Date,
  rangeEnd: Date,
): Highcharts.XAxisPlotLinesOptions[] => dayBoundaries(rangeStart, rangeEnd).map((ts) => ({
  value: ts,
  color: CHART_UI.gridLine,
  width: 1,
  zIndex: 2,
  label: {
    text: new Date(ts).toLocaleDateString('es-CL', { weekday: 'short', day: 'numeric', month: 'short' }),
    style: { color: CHART_UI.dayLabel, fontWeight: '600', fontSize: '11px' },
    x: 6,
    y: 12,
  },
}));

const makeTooltipFormatter = (
  categories: string[],
): Highcharts.TooltipFormatterCallbackFunction => function tooltipFormatter(
  this: Highcharts.TooltipFormatterContextObject,
) {
  const point = this.point as Highcharts.Point & {
    custom?: { kind: string; status: IrrigationStatus };
    start?: number;
    end?: number;
  };
  const c = point.custom;
  if (!c) return '';
  const isReal = c.kind === 'real';
  const isProposal = !isReal && c.status === 'proposal';
  let header: string = TOOLTIP_COLORS.scheduled;
  if (isReal) header = TOOLTIP_COLORS.real;
  else if (isProposal) header = STATUS_COLORS.proposal.border;
  let title = 'Riego Programado';
  if (isReal) title = 'Riego Real';
  else if (isProposal) title = 'Propuesta de Riego';
  const rows = [
    ['Estado', STATUS_LABEL[c.status] || c.status],
    ['Zona', categories[point.y as number] || '—'],
    ['Inicio', fmt(point.start as number)],
    ['Fin', fmt(point.end as number)],
  ].map(([l, v]) => (
    `<tr><td style="color:${TOOLTIP_COLORS.labelText};padding:1px 10px 1px 0;white-space:nowrap;">${l}</td>`
    + `<td style="font-weight:600;text-align:right;white-space:nowrap;">${v}</td></tr>`
  )).join('');
  return `<div style="min-width:210px;">
    <div style="background:${header};color:#fff;font-weight:700;text-align:center;padding:4px 10px;">${title}</div>
    <table style="border-collapse:collapse;font-size:11px;margin:6px 10px 8px;">${rows}</table>
  </div>`;
};

const buildGanttOptions = ({
  zones, scheduled, real, rangeStart, rangeEnd, zoneIndex, peakHour, onPointClick, nowMs,
}: BuildGanttOptionsArgs): Highcharts.Options => {
  const categories = zones.map((z) => z.name);
  const scheduledPoints = buildPoints(scheduled, zoneIndex, 'scheduled', nowMs);
  const realPoints = buildPoints(real, zoneIndex, 'real', nowMs);
  const dayLines = buildDayLines(rangeStart, rangeEnd);
  const peakHourBands = buildPeakHourBands(rangeStart, rangeEnd, peakHour);

  return {
    chart: {
      height: Math.max(CHART_MIN_HEIGHT, CHART_HEADROOM + zones.length * ROW_HEIGHT),
      backgroundColor: CHART_UI.paperBackground,
      style: { fontFamily: 'inherit' },
      spacingTop: 10,
      spacingBottom: 10,
    },
    title: { text: undefined },
    credits: { enabled: false },
    time: { useUTC: false },
    navigator: { enabled: false },
    rangeSelector: { enabled: false },
    scrollbar: { enabled: false },
    legend: { enabled: false },
    xAxis: [{
      min: rangeStart.getTime(),
      max: rangeEnd.getTime(),
      currentDateIndicator: {
        color: NOW_LINE,
        width: 2,
        label: {
          format: 'Ahora',
          style: { color: NOW_LINE, fontWeight: 'bold', fontSize: '11px' },
          y: -6,
        },
      } as Highcharts.CurrentDateIndicatorOptions,
      plotLines: dayLines,
      plotBands: peakHourBands,
      gridLineWidth: 1,
      gridLineColor: CHART_UI.gridLine,
      labels: { style: { fontSize: '11px', color: CHART_UI.axisLabel } },
      tickPixelInterval: TICK_PIXEL_INTERVAL,
    }],
    yAxis: {
      categories,
      min: 0,
      max: Math.max(0, zones.length - 1),
      staticScale: ROW_HEIGHT,
      gridLineColor: CHART_UI.gridLine,
      labels: { style: { fontWeight: '600', color: CHART_UI.yAxisLabel, fontSize: '12px' } },
    },
    tooltip: {
      useHTML: true,
      outside: true,
      backgroundColor: CHART_UI.paperBackground,
      borderColor: TOOLTIP_COLORS.border,
      borderRadius: 6,
      padding: 0,
      formatter: makeTooltipFormatter(categories),
    },
    plotOptions: {
      series: {
        borderRadius: 4,
        pointPadding: POINT_PADDING,
        borderWidth: 1,
        cursor: onPointClick ? 'pointer' : undefined,
        states: { hover: { brightness: HOVER_BRIGHTNESS, borderColor: CHART_UI.hoverBorder } },
        point: {
          events: {
            click() {
              const c = (this as Highcharts.Point & {
                custom?: { kind: 'scheduled' | 'real'; id: number };
              }).custom;
              if (c && onPointClick) onPointClick(c.kind, c.id);
            },
          },
        },
      },
    },
    series: [
      {
        type: 'gantt', name: 'Programado', data: scheduledPoints, dataLabels: { enabled: false },
      },
      {
        type: 'gantt', name: 'Real', data: realPoints, dataLabels: { enabled: false },
      },
    ],
  } as Highcharts.Options;
};

export default buildGanttOptions;
